
// ─── Avatar display helpers ───────────────────────────────────────────────────

export const AVATAR_COLORS = [
  'bg-teal-100 text-teal-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-sky-100 text-sky-700',
  'bg-orange-100 text-orange-700',
  'bg-indigo-100 text-indigo-700',
];

export function getInitials(name: string | null | undefined): string {
  if (!name || !name.trim()) return '?';
  const trimmed = name.trim();
  if (/[\u4e00-\u9fa5]/.test(trimmed)) return trimmed.slice(-2);
  return trimmed
    .split(/\s+/)
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function getAvatarColor(name: string | null | undefined): string {
  if (!name) return AVATAR_COLORS[0];
  const code = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

// ─── File validation ──────────────────────────────────────────────────────────

export const ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/bmp',
];

/** Validate raw file before processing. Returns an error string or null. */
export function validateImageFile(file: File): string | null {
  if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
    return '仅支持 JPG / PNG / WebP / GIF 格式';
  }
  if (file.size > 30 * 1024 * 1024) {
    return '图片不能超过 30MB';
  }
  return null;
}

// ─── Image compression ────────────────────────────────────────────────────────

export interface CompressOptions {
  /** Maximum output dimension (px) - default 1200 */
  maxDim?: number;
  /** Maximum file size in KB - default 1024 (1 MB) */
  maxSizeKB?: number;
  /** Starting quality (0‑1) - default 0.9 */
  startQuality?: number;
}

/**
 * Compress an image Blob to fit within `maxSizeKB` using canvas + iterative
 * quality reduction. Always outputs JPEG.
 */
export function compressImageBlob(
  blob: Blob,
  options: CompressOptions = {},
): Promise<Blob> {
  const { maxDim = 1200, maxSizeKB = 1024, startQuality = 0.9 } = options;

  return new Promise<Blob>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Scale down dimensions while preserving aspect ratio
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      if (w > maxDim || h > maxDim) {
        const ratio = Math.min(maxDim / w, maxDim / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas 不可用')); return; }
      ctx.drawImage(img, 0, 0, w, h);

      const maxBytes = maxSizeKB * 1024;

      const attempt = (quality: number) => {
        canvas.toBlob(
          (result) => {
            if (!result) { reject(new Error('压缩失败')); return; }
            if (result.size <= maxBytes || quality <= 0.05) {
              resolve(result);
            } else {
              // Reduce by 10% each pass, floor at 5%
              attempt(Math.max(quality - 0.1, 0.05));
            }
          },
          'image/jpeg',
          quality,
        );
      };

      attempt(startQuality);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('图片加载失败'));
    };
    img.src = objectUrl;
  });
}

/**
 * Load a File / Blob as a data URL (for canvas / <img> preview).
 */
export function readFileAsDataURL(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });
}
